import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingConfirmPage = ({ action = 'confirm' }) => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const processAction = async () => {
      try {
        const endpoint = action === 'confirm' ? 'confirm' : 'cancel';
        const res = await fetch(`${API_URL}/api/bookings/${bookingId}/${endpoint}?token=${token}`);
        const result = await res.json();
        if (res.ok) {
          setStatus(result.status === 'already_cancelled' ? 'already_cancelled' : action);
          setData(result);
        } else {
          setStatus('error');
          setError(result.detail || 'Une erreur est survenue');
        }
      } catch {
        setStatus('error');
        setError('Impossible de contacter le serveur');
      }
    };
    processAction();
  }, [bookingId, token, action]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #FFF5F7 0%, #FDF2F8 50%, #FCE7F3 100%)',
      padding: '2rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '1.5rem',
        padding: '3rem',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(231, 84, 128, 0.15)'
      }}>
        {status === 'loading' && (
          <>
            <Loader2 size={48} style={{ color: '#E75480', animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '1rem', color: '#666' }}>Traitement en cours...</p>
          </>
        )}

        {status === 'confirm' && (
          <>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: '#dcfce7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <CheckCircle size={44} style={{ color: '#22c55e' }} />
            </div>
            <h1 style={{ color: '#166534', fontSize: '1.5rem', marginBottom: '1rem' }}>
              Présence confirmée !
            </h1>
            <p style={{ color: '#666', lineHeight: 1.6 }}>
              Merci d'avoir confirmé votre rendez-vous.
            </p>
            {data && (
              <div style={{
                background: '#f0fdf4', borderRadius: '12px', padding: '1.25rem',
                margin: '1.5rem 0', borderLeft: '4px solid #22c55e'
              }}>
                <p style={{ margin: '4px 0', fontWeight: 600 }}>{formatDate(data.date)}</p>
                <p style={{ margin: '4px 0', fontSize: '1.2rem', fontWeight: 700, color: '#166534' }}>{data.time}</p>
              </div>
            )}
            <p style={{ color: '#888', fontSize: '0.85rem' }}>À bientôt !</p>
          </>
        )}

        {status === 'cancel' && (
          <>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <XCircle size={44} style={{ color: '#ef4444' }} />
            </div>
            <h1 style={{ color: '#991b1b', fontSize: '1.5rem', marginBottom: '1rem' }}>
              Rendez-vous annulé
            </h1>
            <p style={{ color: '#666', lineHeight: 1.6 }}>
              Votre rendez-vous a été annulé. Merci de nous avoir prévenus.
            </p>
            {data && (
              <div style={{
                background: '#fef2f2', borderRadius: '12px', padding: '1.25rem',
                margin: '1.5rem 0', borderLeft: '4px solid #ef4444', textDecoration: 'line-through', color: '#999'
              }}>
                <p style={{ margin: '4px 0' }}>{formatDate(data.date)}</p>
                <p style={{ margin: '4px 0' }}>{data.time}</p>
              </div>
            )}
            <p style={{ color: '#888', fontSize: '0.85rem' }}>
              Vous pouvez reprendre un rendez-vous à tout moment sur <a href="/" style={{ color: '#E75480' }}>apdr-vae.fr</a>.
            </p>
          </>
        )}

        {status === 'already_cancelled' && (
          <>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <AlertTriangle size={44} style={{ color: '#f59e0b' }} />
            </div>
            <h1 style={{ color: '#92400e', fontSize: '1.5rem', marginBottom: '1rem' }}>
              Rendez-vous déjà annulé
            </h1>
            <p style={{ color: '#666' }}>Ce rendez-vous a déjà été annulé précédemment.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
            }}>
              <AlertTriangle size={44} style={{ color: '#ef4444' }} />
            </div>
            <h1 style={{ color: '#991b1b', fontSize: '1.5rem', marginBottom: '1rem' }}>Erreur</h1>
            <p style={{ color: '#666' }}>{error}</p>
          </>
        )}

        <div style={{ marginTop: '2rem' }}>
          <a href="/" style={{
            display: 'inline-block', padding: '0.75rem 2rem',
            background: '#E75480', color: 'white', borderRadius: '8px',
            textDecoration: 'none', fontWeight: 600
          }}>
            Retour à l'accueil
          </a>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BookingConfirmPage;
